/**
 * Debug Mode Utility
 * 
 * Provides comprehensive error logging and debugging capabilities
 * for troubleshooting application issues in different environments.
 */

// Debug mode configuration
const DEBUG_MODE = import.meta.env.VITE_DEBUG_MODE === 'true' || false;
const ERROR_DISPLAY_DURATION = 30000; // 30 seconds

// Debug levels
export enum DebugLevel {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical'
}

// Debug message interface
interface DebugMessage {
  level: DebugLevel;
  message: string;
  details?: any;
  timestamp: Date;
}

// Store debug messages
const debugMessages: DebugMessage[] = [];

/**
 * Log a debug message with specified level and details
 */
export const logDebug = (level: DebugLevel, message: string, details?: any): void => {
  const debugMessage: DebugMessage = {
    level,
    message,
    details,
    timestamp: new Date()
  };
  
  debugMessages.push(debugMessage);
  
  // Log to console with appropriate formatting
  switch (level) {
    case DebugLevel.INFO:
      console.log(`🔍 [INFO] ${message}`, details || '');
      break;
    case DebugLevel.WARNING:
      console.warn(`⚠️ [WARNING] ${message}`, details || '');
      break;
    case DebugLevel.ERROR:
      console.error(`❌ [ERROR] ${message}`, details || '');
      break;
    case DebugLevel.CRITICAL:
      console.error(`🔥 [CRITICAL] ${message}`, details || '');
      // Display on screen for critical errors if in debug mode
      if (DEBUG_MODE) {
        displayErrorOnScreen(message, details);
      }
      break;
  }
};

/**
 * Display error message on screen for critical errors
 */
const displayErrorOnScreen = (message: string, details?: any): void => {
  // Create error container if it doesn't exist
  let errorContainer = document.getElementById('debug-error-container');
  if (!errorContainer) {
    errorContainer = document.createElement('div');
    errorContainer.id = 'debug-error-container';
    errorContainer.style.position = 'fixed';
    errorContainer.style.top = '0';
    errorContainer.style.left = '0';
    errorContainer.style.right = '0';
    errorContainer.style.zIndex = '9999';
    errorContainer.style.display = 'flex';
    errorContainer.style.flexDirection = 'column';
    errorContainer.style.gap = '10px';
    errorContainer.style.padding = '10px';
    document.body.appendChild(errorContainer);
  }
  
  // Create error message element
  const errorElement = document.createElement('div');
  errorElement.style.backgroundColor = '#ff3333';
  errorElement.style.color = 'white';
  errorElement.style.padding = '10px';
  errorElement.style.borderRadius = '4px';
  errorElement.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
  errorElement.style.fontFamily = 'Arial, sans-serif';
  errorElement.style.fontSize = '14px';
  
  // Add error message content
  const messageElement = document.createElement('div');
  messageElement.style.fontWeight = 'bold';
  messageElement.textContent = message;
  errorElement.appendChild(messageElement);
  
  // Add error details if available
  if (details) {
    const detailsElement = document.createElement('pre');
    detailsElement.style.marginTop = '5px';
    detailsElement.style.padding = '5px';
    detailsElement.style.backgroundColor = 'rgba(0,0,0,0.1)';
    detailsElement.style.borderRadius = '2px';
    detailsElement.style.overflow = 'auto';
    detailsElement.style.maxHeight = '200px';
    detailsElement.textContent = typeof details === 'object' ? JSON.stringify(details, null, 2) : String(details);
    errorElement.appendChild(detailsElement);
  }
  
  // Add close button
  const closeButton = document.createElement('button');
  closeButton.textContent = '×';
  closeButton.style.position = 'absolute';
  closeButton.style.top = '5px';
  closeButton.style.right = '5px';
  closeButton.style.background = 'none';
  closeButton.style.border = 'none';
  closeButton.style.color = 'white';
  closeButton.style.fontSize = '20px';
  closeButton.style.cursor = 'pointer';
  closeButton.onclick = () => errorElement.remove();
  errorElement.appendChild(closeButton);
  
  // Add to container
  errorContainer.appendChild(errorElement);
  
  // Auto-remove after duration
  setTimeout(() => {
    if (errorElement.parentNode) {
      errorElement.remove();
    }
  }, ERROR_DISPLAY_DURATION);
};

/**
 * Get all debug messages
 */
export const getDebugMessages = (): DebugMessage[] => {
  return [...debugMessages];
};

/**
 * Clear all debug messages
 */
export const clearDebugMessages = (): void => {
  debugMessages.length = 0;
};

/**
 * Initialize debug mode
 */
export const initializeDebugMode = (): void => {
  if (DEBUG_MODE) {
    logDebug(DebugLevel.INFO, 'Debug mode initialized');
    
    // Log environment information
    logDebug(DebugLevel.INFO, 'Environment Information', {
      mode: import.meta.env.MODE,
      apiUrl: import.meta.env.VITE_API_URL,
      brainApiUrl: import.meta.env.VITE_BRAIN_API_URL,
      wsBasePath: import.meta.env.VITE_WS_BASE_PATH,
      mockAuth: import.meta.env.VITE_USE_MOCK_AUTH,
      buildId: import.meta.env.VITE_BUILD_ID,
      buildTimestamp: import.meta.env.VITE_BUILD_TIMESTAMP,
      userAgent: navigator.userAgent,
      hostname: window.location.hostname
    });
    
    // Add global error handler
    window.addEventListener('error', (event) => {
      logDebug(DebugLevel.CRITICAL, 'Unhandled error', {
        message: event.error?.message || 'Unknown error',
        stack: event.error?.stack,
        source: event.filename,
        line: event.lineno,
        column: event.colno
      });
    });
    
    // Add unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      logDebug(DebugLevel.CRITICAL, 'Unhandled promise rejection', {
        message: event.reason?.message || 'Unknown reason',
        stack: event.reason?.stack,
        reason: event.reason
      });
    });
    
    console.log('🐛 Debug mode is ENABLED - Performance may be affected');
  }
};

// Export debug mode status
export const isDebugMode = (): boolean => DEBUG_MODE;

export default {
  logDebug,
  getDebugMessages,
  clearDebugMessages,
  initializeDebugMode,
  isDebugMode,
  DebugLevel
};
